/**
 * The live log panel (§A.5, requirement 4): a virtualized, filterable
 * stream of every published event kind, each line showing its provenance
 * (seq, event/knowledge time, and — for a v2 role — the full payload digest;
 * see `src/types/events.ts`), a scrubber keyed on `seq` that drives every
 * other panel via the shared `PlaybackStore`, and a "follow live" mode that
 * keeps polling `RoleSnapshot.events()` for growth (v1: re-parses the tail;
 * v2: fetches only chunks newer than its own since-seq cursor).
 *
 * Owner directive 2026-09-27 ("the layout was still not improved ... the
 * live log especially"): this panel used to show ~19 rows in a narrow
 * strip, with timestamps truncated to "2026-09-2", event kinds running into
 * the dates, and filter chips wrapping into two lines. It now lives in a
 * full-height rail/dock (`OrganismScreen`/`style.css`) and this file adds:
 * a fixed-width `HH:MM:SS` time column with day dividers, a category-
 * coloured monospaced kind column, a one-line truncated payload preview,
 * a click-to-open detail drawer, and a one-row filter toolbar that
 * overflows extra chips into a "+N more" menu instead of wrapping
 * (`src/log/logFormat.ts` carries the pure formatting/overflow math).
 */

import type { RoleSnapshot } from "../data/snapshotIndex";
import { EventStreamCursor } from "../data/eventStream";
import { groupForKind, type LogEvent, type LogEventGroup } from "../types/events";
import { Poller, freshnessBadgeLabel } from "../data/polling";
import { isNotPublishedYet } from "../data/fetchJson";
import { config } from "../config";
import type { PlaybackStore } from "../state/PlaybackStore";
import { computeFollowScrollTop, computeVirtualRange } from "./virtualList";
import { attachTooltip } from "../ui/tooltip";
import {
  activityIconKind,
  activitySentence,
  buildActivityRowItems,
  computeVisibleChipCount,
  formatClockTime,
  logKindColorClass,
  payloadPreviewText,
  type ActivityFilter,
  type LogRowItem,
} from "./logFormat";
import { renderActivityIcon } from "./activityIcons";

// 19px, not a round 20: at exactly 1440x900 (the owner's own reported-cramped
// screenshot size, and this pass's ">=40 visible rows" acceptance target)
// every pixel of chrome above the list (fleetbar, panel header, filter
// toolbar) competes with row count for the same fixed 900px — 19px rows
// clear 40 visible rows at that size with the rest of this pass's chrome;
// 20px landed at 38.
const ROW_HEIGHT_PX = 19;
const CHIP_GAP_PX = 4;
const MORE_BUTTON_WIDTH_PX = 46;

/** Groups the filter chips into a few logical clusters instead of one flat,
 * alphabetically-sorted row (declutter requirement: "the log panel's filter
 * chips grouped and less noisy") — a busy real log easily has 8-10 distinct
 * kinds present at once. Anything not named here falls into its own
 * trailing cluster, so a future/unknown group is never silently dropped. */
const FILTER_CLUSTERS: readonly (readonly LogEventGroup[])[] = [
  ["brain", "market"],
  ["portfolio", "life", "family"],
  ["order", "reconcile"],
  ["semantic", "research"],
  ["runtime", "observatory"],
];

const GROUP_TOOLTIPS: Readonly<Record<ActivityFilter, string>> = {
  signal:
    "The default view: decisions, orders/fills, portfolio, and life — everything else is one click away.",
  all: "Show every event kind, including per-cycle market chatter and infrastructure bookkeeping.",
  system:
    "This telemetry pipeline's own bookkeeping and runtime lifecycle events — hidden by default.",
  brain: "brain.wake, brain.plasticity, brain.model_trained/activated.",
  market: "market.view, market.orderbook, calendar session transitions.",
  portfolio: "portfolio.snapshot and env_/fee/fill ledger postings.",
  life: "life.assessment and lifecycle stage transitions.",
  family: "family.assessment from the mating coordinator.",
  order: "Order lifecycle transitions (placed, filled, cancelled).",
  reconcile: "Broker/ledger reconciliation status.",
  semantic: "Semantic-organ calls: route, latency, cost, accepted/rejected.",
  research: "research.document_ingested, research.reflection, and related events.",
  runtime: "runtime.heartbeat and loop_/worker lifecycle events.",
  observatory: "This telemetry pipeline's own bookkeeping (byte accounting, retention).",
  lineage: "Lineage/procreation-adjacent events.",
  other: "Anything not covered by another group.",
};

interface ChipDef {
  readonly group: ActivityFilter;
  readonly clusterStart: boolean;
}

function clusterOrder(groups: readonly LogEventGroup[]): LogEventGroup[][] {
  const remaining = new Set(groups);
  const clusters: LogEventGroup[][] = [];
  for (const cluster of FILTER_CLUSTERS) {
    const present = cluster.filter((g) => remaining.has(g));
    if (present.length > 0) {
      clusters.push(present);
      for (const g of present) remaining.delete(g);
    }
  }
  if (remaining.size > 0) clusters.push(Array.from(remaining).sort());
  return clusters;
}

/** Chip order with the active filter (if any) pulled forward right after
 * "signal"/"all" — so switching to a filter that happens to be deep in the
 * cluster list never immediately hides its own active chip behind
 * "+N more". `"signal"` (the default view) and `"system"` (the explicit
 * escape hatch for runtime/observatory chatter — orchestrator course
 * correction, 2026-09-28) are fixed leading chips, same treatment "all"
 * already had. */
function chipOrder(groupsPresent: Set<LogEventGroup>, activeFilter: ActivityFilter): ChipDef[] {
  const clusters = clusterOrder(Array.from(groupsPresent));
  const flat: LogEventGroup[] = [];
  for (const cluster of clusters) for (const g of cluster) flat.push(g);

  const ordered: LogEventGroup[] =
    activeFilter !== "all" &&
    activeFilter !== "signal" &&
    activeFilter !== "system" &&
    flat.includes(activeFilter)
      ? [activeFilter, ...flat.filter((g) => g !== activeFilter)]
      : flat;

  const clusterStarts = new Set<LogEventGroup>();
  for (const cluster of clusters) {
    const first = cluster[0];
    if (first) clusterStarts.add(first);
  }

  return [
    { group: "signal" as const, clusterStart: false },
    { group: "all" as const, clusterStart: false },
    { group: "system" as const, clusterStart: false },
    ...ordered.map((group) => ({
      group,
      clusterStart:
        clusterStarts.has(group) && group === ordered[0] ? false : clusterStarts.has(group),
    })),
  ];
}

export class LogPanel {
  private cursor = new EventStreamCursor([]);
  // "signal" (decisions/orders/portfolio/life), not "all" — orchestrator
  // course correction, 2026-09-28: "Activity feed must be signal, not
  // noise". Every event still exists and is one click away via the "all"/
  // "system"/per-group chips; nothing is ever truly hidden, just not the
  // default view.
  private activeFilter: ActivityFilter = "signal";
  private following = true;
  // The scrollTop this panel's OWN last follow-live render assigned, so the
  // scroll listener (line ~260) can tell "the render loop just pinned this
  // to the bottom" apart from "the user just dragged the scrollbar while
  // still following" (code review round 2: without this, ANY scroll event
  // while `following` is true — including the user's own manual drag — was
  // treated identically to the programmatic one, so `renderRows()` snapped
  // scrollTop straight back to the bottom before the user could read
  // anything above the newest row).
  private lastFollowScrollTop: number | null = null;
  private poller: Poller<readonly LogEvent[]> | null = null;
  private unsubscribePlayback: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private selectedSeq: number | null = null;
  private moreMenuOpen = false;

  private listEl: HTMLDivElement | null = null;
  private innerEl: HTMLDivElement | null = null;
  private filtersEl: HTMLDivElement | null = null;
  private moreButtonEl: HTMLButtonElement | null = null;
  private moreMenuEl: HTMLDivElement | null = null;
  private freshnessEl: HTMLSpanElement | null = null;
  private followButton: HTMLButtonElement | null = null;
  private drawerEl: HTMLDivElement | null = null;
  private readonly onDocumentClick = (event: MouseEvent): void => {
    if (!this.moreMenuOpen || !this.moreMenuEl || !this.moreButtonEl) return;
    const target = event.target as Node;
    if (this.moreMenuEl.contains(target) || this.moreButtonEl.contains(target)) return;
    this.moreMenuOpen = false;
    this.moreMenuEl.hidden = true;
  };

  constructor(
    private readonly container: HTMLElement,
    private readonly role: RoleSnapshot,
    private readonly playback: PlaybackStore,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    this.buildDom();

    const initialEvents = await this.role.events();
    this.cursor = new EventStreamCursor(initialEvents);
    this.render();

    this.poller = new Poller<readonly LogEvent[]>(
      async () => {
        const events = await this.role.events();
        const last = events[events.length - 1];
        return { key: `${events.length}:${last ? last.seq : -1}`, value: events };
      },
      (snapshot) => {
        if (snapshot.value) {
          this.cursor = new EventStreamCursor(snapshot.value);
          if (this.following) this.render();
        }
        if (this.freshnessEl) {
          this.freshnessEl.textContent = freshnessBadgeLabel(
            snapshot.status,
            snapshot.lastSuccessAt,
          );
        }
      },
      { intervalMs: config.pollIntervalMs, backoffMaxMs: config.pollBackoffMaxMs },
      isNotPublishedYet,
    );
    this.poller.start();

    this.unsubscribePlayback = this.playback.subscribe((state) => {
      this.following = state.following;
      if (this.followButton) {
        this.followButton.classList.toggle("obs-active", state.following);
        this.followButton.textContent = state.following
          ? "following live — pause"
          : "paused — follow live";
      }
      this.renderRows();
    });

    if (this.filtersEl && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.renderFilters());
      this.resizeObserver.observe(this.filtersEl);
    }
    document.addEventListener("click", this.onDocumentClick);
  }

  private buildDom(): void {
    const root = document.createElement("div");
    root.className = "obs-log";

    const filters = document.createElement("div");
    filters.className = "obs-log-filters";
    this.filtersEl = filters;

    const drawer = document.createElement("div");
    drawer.className = "obs-log-drawer";
    drawer.hidden = true;
    this.drawerEl = drawer;

    const list = document.createElement("div");
    list.className = "obs-log-list";
    const inner = document.createElement("div");
    inner.style.position = "relative";
    list.appendChild(inner);
    this.listEl = list;
    this.innerEl = inner;
    list.addEventListener("scroll", () => {
      // A scroll event while following live, landing anywhere other than
      // where this panel's own last render pinned it, is the user dragging
      // the scrollbar — pause (mirrors the "follow live" button's own pause
      // action) instead of snapping straight back to the bottom on the next
      // render (code review round 2).
      //
      // OQA: a scroll that still lands at the bottom is NOT the user leaving live —
      // the browser re-clamps scrollTop while the layout settles on page load, which
      // used to pause the SHARED playback store (the brain stopped following new
      // wakes and the follow button read "paused" on load).
      const atBottom =
        this.listEl !== null &&
        this.listEl.clientHeight > 0 &&
        this.listEl.scrollHeight - this.listEl.scrollTop - this.listEl.clientHeight <=
          ROW_HEIGHT_PX;
      if (
        this.following &&
        this.listEl !== null &&
        !atBottom &&
        this.listEl.scrollTop !== this.lastFollowScrollTop
      ) {
        this.playback.setSeq(this.cursor.maxSeq);
        return;
      }
      this.renderRows();
    });

    const footer = document.createElement("div");
    footer.className = "obs-scrubber-bar";
    const follow = document.createElement("button");
    follow.className = "obs-follow-toggle obs-active";
    follow.textContent = "follow live";
    attachTooltip(
      follow,
      "Toggle following the newest events as they arrive vs. staying at the scrubbed position.",
    );
    follow.addEventListener("click", () => {
      if (this.following) {
        this.playback.setSeq(this.cursor.maxSeq);
      } else {
        this.playback.followLive();
      }
    });
    this.followButton = follow;
    const freshness = document.createElement("span");
    freshness.className = "obs-freshness";
    this.freshnessEl = freshness;
    footer.appendChild(follow);
    footer.appendChild(freshness);

    root.appendChild(filters);
    root.appendChild(drawer);
    root.appendChild(list);
    root.appendChild(footer);
    this.container.appendChild(root);
  }

  private render(): void {
    this.renderFilters();
    this.renderRows();
  }

  /** Rebuilds the filter toolbar: one row of chips, with whatever doesn't
   * fit collected behind a "+N more" button instead of wrapping to a second
   * line (owner directive: "filter chips wrap into two lines"). Measures
   * real chip widths in a throwaway pass, then re-renders with the overflow
   * decision applied — simplest way to get exact pixel widths without
   * hand-estimating text metrics. */
  private renderFilters(): void {
    if (!this.filtersEl) return;
    const container = this.filtersEl;
    container.innerHTML = "";

    const groupsPresent = new Set<LogEventGroup>();
    for (const event of this.cursor.all) groupsPresent.add(groupForKind(event.kind));
    const chips = chipOrder(groupsPresent, this.activeFilter);

    const makeChip = (chip: ChipDef): HTMLButtonElement => {
      const button = document.createElement("button");
      button.className = "obs-log-chip";
      if (chip.clusterStart) button.classList.add("obs-log-chip-cluster-start");
      button.textContent = chip.group;
      button.classList.toggle("obs-active", chip.group === this.activeFilter);
      attachTooltip(button, GROUP_TOOLTIPS[chip.group]);
      button.addEventListener("click", () => {
        this.activeFilter = chip.group;
        this.moreMenuOpen = false;
        this.render();
      });
      return button;
    };

    // Measuring pass: render every chip so the browser lays them out and we
    // can read real widths, without yet deciding what overflows.
    const rendered = chips.map((chip) => ({ chip, el: makeChip(chip) }));
    for (const { el } of rendered) container.appendChild(el);

    const containerWidth = container.clientWidth || 600;
    const widths = rendered.map(({ el }) => el.getBoundingClientRect().width);
    const visibleCount = computeVisibleChipCount(
      widths,
      containerWidth,
      MORE_BUTTON_WIDTH_PX,
      CHIP_GAP_PX,
    );
    const overflow = rendered.slice(visibleCount);

    if (overflow.length === 0) {
      this.moreButtonEl = null;
      this.moreMenuEl = null;
      return;
    }

    // Re-render: drop the overflowing chips' DOM nodes, add a "+N more"
    // button that opens a small menu holding exactly those chips.
    for (const { el } of overflow) el.remove();

    const more = document.createElement("button");
    more.className = "obs-log-chip obs-log-more";
    more.textContent = `+${overflow.length} more`;
    attachTooltip(
      more,
      `${overflow.length} more filter${overflow.length === 1 ? "" : "s"}: ${overflow.map((o) => o.chip.group).join(", ")}`,
    );
    more.addEventListener("click", (event) => {
      event.stopPropagation();
      this.moreMenuOpen = !this.moreMenuOpen;
      menu.hidden = !this.moreMenuOpen;
    });
    this.moreButtonEl = more;
    container.appendChild(more);

    const menu = document.createElement("div");
    menu.className = "obs-log-more-menu";
    menu.hidden = !this.moreMenuOpen;
    for (const { chip } of overflow) menu.appendChild(makeChip(chip));
    this.moreMenuEl = menu;
    container.appendChild(menu);
  }

  private renderRows(): void {
    if (!this.listEl || !this.innerEl) return;
    // The activity feed's own grouping/filtering (orchestrator course
    // correction, 2026-09-28): applies `activeFilter`, groups rows under a
    // wake header, and collapses per-cycle market.view chatter into one
    // summary line — see logFormat.ts's buildActivityRowItems doc comment.
    const items: readonly LogRowItem[] = buildActivityRowItems(this.cursor.all, this.activeFilter);
    const clientHeightPx = this.listEl.clientHeight || 300;
    // Follow-live mode (owner bug report 2026-09-30: the live activity log
    // always showed the oldest events at the top, never the newest — this
    // element's `scrollTop` was only ever read here, never set, so a
    // freshly-rendered scrollable div sat at 0 forever since rows are
    // oldest-first). While following, pin the viewport to the newest rows.
    // The target scrollTop is computed up front (rather than only assigned
    // after rendering) so this same pass paints the rows that will actually
    // be visible once the real assignment below takes effect, instead of a
    // stale window followed by a flash on the next scroll event. This never
    // fights a user who has paused and manually scrolled up: it only
    // applies while `this.following` is true (kept in sync with the
    // "follow live" button handler and the playback-store subscription in
    // `mount()`).
    const followScrollTop = this.following
      ? computeFollowScrollTop(items.length, ROW_HEIGHT_PX, clientHeightPx)
      : null;
    const range = computeVirtualRange(
      items.length,
      ROW_HEIGHT_PX,
      followScrollTop ?? this.listEl.scrollTop,
      clientHeightPx,
    );
    this.innerEl.style.height = `${range.totalHeightPx}px`;
    // The real DOM scrollTop assignment must come after the height above is
    // applied: a browser clamps `scrollTop` to the scrollable element's
    // current `scrollHeight`, which is driven by `innerEl`'s own height —
    // assigning it too early would silently clamp short of the true bottom
    // right after the log grows.
    this.lastFollowScrollTop = followScrollTop;
    if (followScrollTop !== null) this.listEl.scrollTop = followScrollTop;
    this.innerEl.innerHTML = "";
    const state = this.playback.current;
    const highlightSeq = state.following ? this.cursor.maxSeq : (state.seq ?? this.cursor.maxSeq);

    for (let i = range.startIndex; i < range.endIndex; i += 1) {
      const item = items[i];
      if (!item) continue;

      if (item.type === "divider") {
        const divider = document.createElement("div");
        divider.className = "obs-log-day-divider";
        divider.style.top = `${i * ROW_HEIGHT_PX}px`;
        divider.style.height = `${ROW_HEIGHT_PX}px`;
        divider.textContent = item.label;
        this.innerEl.appendChild(divider);
        continue;
      }

      if (item.type === "wake-header") {
        const header = document.createElement("div");
        header.className = "obs-log-wake-header";
        header.style.top = `${i * ROW_HEIGHT_PX}px`;
        header.style.height = `${ROW_HEIGHT_PX}px`;
        const timeEl = document.createElement("span");
        timeEl.className = "obs-log-time";
        timeEl.textContent = item.time;
        const labelEl = document.createElement("span");
        labelEl.textContent = item.label;
        header.appendChild(timeEl);
        header.appendChild(labelEl);
        this.innerEl.appendChild(header);
        continue;
      }

      const event = item.event;
      const row = document.createElement("div");
      row.className = "obs-log-row";
      row.style.top = `${i * ROW_HEIGHT_PX}px`;
      row.style.height = `${ROW_HEIGHT_PX}px`;
      if (event.seq === highlightSeq) row.classList.add("obs-selected");
      if (event.seq === this.selectedSeq) row.classList.add("obs-drawer-open");

      const timeEl = document.createElement("span");
      timeEl.className = "obs-log-time";
      timeEl.textContent = formatClockTime(event.eventTime || event.recordedTime);

      const seqEl = document.createElement("span");
      seqEl.className = "obs-log-seq";
      seqEl.textContent = event.seq >= 0 ? String(event.seq) : "-";

      // Activity feed (DIRECTION.md §1.3): a colour-coded category icon +
      // a human sentence, replacing the raw-JSON preview and the bare
      // technical `kind` text — the kind still shows in this row's hover
      // tooltip (`hoverText`) and the detail drawer's header, so nothing is
      // lost, it just isn't the primary thing a scanning eye reads anymore.
      const iconEl = document.createElement("span");
      iconEl.className = `obs-log-icon ${logKindColorClass(event.kind)}`;
      iconEl.appendChild(renderActivityIcon(activityIconKind(event)));

      const sentenceEl = document.createElement("span");
      sentenceEl.className = "obs-log-sentence";
      sentenceEl.textContent = activitySentence(event);

      row.appendChild(timeEl);
      row.appendChild(seqEl);
      row.appendChild(iconEl);
      row.appendChild(sentenceEl);

      attachTooltip(row, () => this.hoverText(event));

      row.addEventListener("click", () => {
        if (event.seq >= 0) this.playback.setSeq(event.seq);
        this.selectedSeq = this.selectedSeq === event.seq ? null : event.seq;
        this.renderDrawer(this.selectedSeq === event.seq ? event : null);
        this.renderRows();
      });
      this.innerEl.appendChild(row);
    }
  }

  /** Hover preview text (owner requirement: "one-line payload preview
   * truncated ... with a hover/click detail drawer"). Hover is the cheap,
   * transient look; click (`renderDrawer`) is the persistent, full one. */
  private hoverText(event: LogEvent): string {
    const time = event.eventTime || event.recordedTime || "unknown time";
    const preview = payloadPreviewText(event);
    return preview
      ? `${event.kind} · seq ${event.seq} · ${time}\n${preview}`
      : `${event.kind} · seq ${event.seq} · ${time}`;
  }

  /** The click-to-open detail drawer: full provenance (seq, event/knowledge/
   * recorded time, payload digest) plus a pretty-printed payload for a v2
   * event, or the raw source line for a v1 one — never fabricated fields. */
  private renderDrawer(event: LogEvent | null): void {
    if (!this.drawerEl) return;
    if (!event) {
      this.drawerEl.hidden = true;
      this.drawerEl.innerHTML = "";
      return;
    }
    this.drawerEl.hidden = false;
    this.drawerEl.innerHTML = "";

    const head = document.createElement("div");
    head.className = "obs-log-drawer-head";
    const kind = document.createElement("span");
    kind.className = `obs-log-kind ${logKindColorClass(event.kind)}`;
    kind.textContent = event.kind;
    const seq = document.createElement("span");
    seq.className = "obs-log-drawer-seq";
    seq.textContent = event.seq >= 0 ? `seq ${event.seq}` : "unparsed line";
    const close = document.createElement("button");
    close.className = "obs-log-drawer-close";
    close.textContent = "×";
    attachTooltip(close, "Close this event's detail drawer.");
    close.addEventListener("click", () => {
      this.selectedSeq = null;
      this.renderDrawer(null);
      this.renderRows();
    });
    head.appendChild(kind);
    head.appendChild(seq);
    head.appendChild(close);
    this.drawerEl.appendChild(head);

    const meta = document.createElement("div");
    meta.className = "obs-log-drawer-meta";
    const metaRows: [string, string][] = [
      ["event_time", event.eventTime || "—"],
      ["knowledge_time", event.knowledgeTime ?? "—"],
      ["recorded_time", event.recordedTime || "—"],
      ["digest", event.digest ?? "—"],
    ];
    for (const [label, value] of metaRows) {
      const row = document.createElement("div");
      row.className = "obs-log-drawer-meta-row";
      const labelEl = document.createElement("span");
      labelEl.className = "obs-log-drawer-meta-label";
      labelEl.textContent = label;
      const valueEl = document.createElement("span");
      valueEl.textContent = value;
      row.appendChild(labelEl);
      row.appendChild(valueEl);
      meta.appendChild(row);
    }
    this.drawerEl.appendChild(meta);

    const body = document.createElement("pre");
    body.className = "obs-log-drawer-payload";
    body.textContent = event.payload ? JSON.stringify(event.payload, null, 2) : event.raw;
    this.drawerEl.appendChild(body);
  }

  dispose(): void {
    this.poller?.stop();
    this.unsubscribePlayback?.();
    this.resizeObserver?.disconnect();
    document.removeEventListener("click", this.onDocumentClick);
  }
}
